import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import { API_BASE_URL } from '../utils/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { PageContent } from '../components/Layout'
import AutomationRuleBuilder from '../components/AutomationRuleBuilder'
import EnhancedAutomationRuleBuilder from '../components/EnhancedAutomationRuleBuilder'
import NotificationTemplateEditor from '../components/NotificationTemplateEditor'
import StatusRulesSection from '../components/StatusRulesSection'
import { 
  PlusIcon, 
  EditIcon, 
  TrashIcon,
  SaveIcon,
  XIcon,
  GripVerticalIcon,
  SettingsIcon,
  ChartIcon,
  EllipsisVerticalIcon,
  CogIcon,
  ChevronLeftIcon
} from '../components/Icons'

interface DealType {
  id: number
  name: string
  name_fr?: string
  supports_commercial: boolean
  supports_residential: boolean
  position: number
  is_active: boolean
}

interface WorkflowTemplate {
  id: number
  name: string
  description?: string
  deal_type_id: number
  deal_version: 'commercial' | 'residential'
  deal_versions?: ('commercial' | 'residential')[]
  deal_types?: { id: number; name: string; name_fr?: string }[]
  applies_to_all_deal_types?: boolean
  is_active: boolean
  deal_type_name: string
  deal_type_name_fr?: string
  step_count: number
  created_at: string
  updated_at: string
}

interface WorkflowStep {
  id: number
  workflow_template_id: number
  name: string
  description?: string
  color: string
  position: number
  is_start_step: boolean
  is_end_step: boolean
  metadata?: any
  automations?: WorkflowAutomation[]
}

interface WorkflowAutomation {
  id: number
  workflow_step_id: number
  trigger_event: 'on_enter' | 'on_exit' | 'after_delay'
  action_type: 'send_email' | 'send_sms' | 'create_task' | 'send_notification' | 'update_status' | 'log_activity' | 'assign_deal' | 'external_api_call' | 'set_reminder' | 'update_financial'
  action_config: any
  delay_hours: number
  is_active: boolean
}

interface NotificationTemplate {
  id: number
  name: string
  template_type: 'email' | 'sms' | 'internal'
  subject?: string
  content: string
  variables: string[]
}

interface DealStatus {
  id: number
  name: string
  name_fr?: string
  color: string
  position: number
  is_lead: boolean
  is_submitted: boolean
  is_approved: boolean
  is_funded: boolean
  is_closed: boolean
  is_cancelled: boolean
  is_archived: boolean
}

const WorkflowManagement: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [dealTypes, setDealTypes] = useState<DealType[]>([])
  const [dealStatuses, setDealStatuses] = useState<DealStatus[]>([])
  const [workflowTemplates, setWorkflowTemplates] = useState<WorkflowTemplate[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null)
  const [templateSteps, setTemplateSteps] = useState<WorkflowStep[]>([])
  const [notificationTemplates, setNotificationTemplates] = useState<NotificationTemplate[]>([])
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Modal states
  const [showCreateTemplate, setShowCreateTemplate] = useState(false)
  const [showEditTemplate, setShowEditTemplate] = useState(false)
  const [editingWorkflowTemplate, setEditingWorkflowTemplate] = useState<WorkflowTemplate | null>(null)
  const [showStepEditor, setShowStepEditor] = useState(false)
  const [editingStep, setEditingStep] = useState<WorkflowStep | null>(null)
  const [showAutomationEditor, setShowAutomationEditor] = useState(false)
  const [editingAutomation, setEditingAutomation] = useState<WorkflowAutomation | null>(null)
  const [showTemplateEditor, setShowTemplateEditor] = useState(false)
  const [editingNotificationTemplate, setEditingNotificationTemplate] = useState<NotificationTemplate | null>(null)
  const [selectedStepForAutomation, setSelectedStepForAutomation] = useState<WorkflowStep | null>(null)

  // Form states
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    deal_type_ids: [] as number[],
    include_commercial: true,
    include_residential: true
  })
  const [newStep, setNewStep] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    is_start_step: false,
    is_end_step: false
  })
  const [newAutomation, setNewAutomation] = useState({
    trigger_event: 'on_enter' as 'on_enter' | 'on_exit' | 'after_delay',
    action_type: 'send_email' as 'send_email' | 'send_sms' | 'create_task' | 'send_notification' | 'update_status' | 'log_activity' | 'assign_deal' | 'external_api_call' | 'set_reminder' | 'update_financial',
    action_config: {},
    delay_hours: 0,
    is_active: true
  })

  useEffect(() => {
    fetchInitialData()
  }, [])

  // Handle clicking outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = () => {
      // Close all dropdowns
      const dropdowns = document.querySelectorAll('[id^="template-dropdown-"]')
      dropdowns.forEach(dropdown => {
        dropdown.classList.add('hidden')
      })
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 5000)
  }

  const fetchInitialData = async () => {
    try {
      setLoading(true)
      await Promise.all([
        fetchDealTypes(),
        fetchDealStatuses(),
        fetchWorkflowTemplates(),
        fetchNotificationTemplates()
      ])
    } catch (error) {
      console.error('Error fetching initial data:', error)
      showMessage('error', t('settings.workflowManagement.messages.loadError'))
    } finally {
      setLoading(false)
    }
  }

  const fetchDealTypes = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/settings/deal-types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setDealTypes(data.deal_types || [])
      }
    } catch (error) {
      console.error('Error fetching deal types:', error)
    }
  }

  const fetchDealStatuses = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/deals/statuses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setDealStatuses(data.statuses || [])
      }
    } catch (error) {
      console.error('Error fetching deal statuses:', error)
    }
  }

  const fetchWorkflowTemplates = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/workflows/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setWorkflowTemplates(data.templates || [])
      }
    } catch (error) {
      console.error('Error fetching workflow templates:', error)
    }
  }

  const fetchTemplateSteps = async (templateId: number) => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/workflows/templates/${templateId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setTemplateSteps(data.template.steps || [])
      }
    } catch (error) {
      console.error('Error fetching template steps:', error)
    }
  }

  const fetchNotificationTemplates = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/workflows/notification-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setNotificationTemplates(data.templates || [])
      }
    } catch (error) {
      console.error('Error fetching notification templates:', error)
    }
  }

  const handleCreateTemplate = async () => {
    try {
      const token = localStorage.getItem('token')
      
      // Build payload with selected deal types and versions
      const payload = {
        name: newTemplate.name,
        description: newTemplate.description,
        deal_type_ids: newTemplate.deal_type_ids.length > 0 ? newTemplate.deal_type_ids : [],
        include_commercial: newTemplate.include_commercial,
        include_residential: newTemplate.include_residential
      }
      
      const response = await fetch(`${API_BASE_URL}/api/workflows/templates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (response.ok) {
        const data = await response.json()
        // Now we receive a single template that applies to all selected deal types/versions
        if (data.template) {
          setWorkflowTemplates(prev => [...prev, data.template])
        }
        setShowCreateTemplate(false)
        resetNewTemplate()
        showMessage('success', t('settings.workflowManagement.messages.templateCreated'))
      } else {
        const error = await response.json()
        showMessage('error', error.message || t('settings.workflowManagement.messages.createError'))
      }
    } catch (error) {
      console.error('Error creating template:', error)
      showMessage('error', t('settings.workflowManagement.messages.createError'))
    }
  }
  
  const resetNewTemplate = () => {
    setNewTemplate({
      name: '',
      description: '',
      deal_type_ids: [],
      include_commercial: true,
      include_residential: true
    })
  }

  const handleEditTemplate = async () => {
    if (!editingWorkflowTemplate) return

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/workflows/templates/${editingWorkflowTemplate.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newTemplate.name,
          description: newTemplate.description,
          is_active: true
        })
      })

      if (response.ok) {
        const data = await response.json()
        setWorkflowTemplates(prev => 
          prev.map(template => 
            template.id === editingWorkflowTemplate.id ? data.template : template
          )
        )
        
        // Update selected template if it's the one being edited
        if (selectedTemplate?.id === editingWorkflowTemplate.id) {
          setSelectedTemplate(data.template)
        }

        setShowEditTemplate(false)
        setEditingWorkflowTemplate(null)
        resetNewTemplate()
        showMessage('success', 'Workflow template updated successfully!')
      } else {
        const error = await response.json()
        showMessage('error', error.message || 'Failed to update workflow template')
      }
    } catch (error) {
      console.error('Error updating template:', error)
      showMessage('error', 'Failed to update workflow template')
    }
  }

  const handleDeleteTemplate = async (template: WorkflowTemplate) => {
    if (!confirm(`Are you sure you want to delete the workflow template "${template.name}"? This action cannot be undone.`)) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/workflows/templates/${template.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        setWorkflowTemplates(prev => prev.filter(t => t.id !== template.id))
        
        // Clear selected template if it's the one being deleted
        if (selectedTemplate?.id === template.id) {
          setSelectedTemplate(null)
          setTemplateSteps([])
        }

        showMessage('success', 'Workflow template deleted successfully!')
      } else {
        const error = await response.json()
        showMessage('error', error.message || 'Failed to delete workflow template')
      }
    } catch (error) {
      console.error('Error deleting template:', error)
      showMessage('error', 'Failed to delete workflow template')
    }
  }

  const handleSelectTemplate = async (template: WorkflowTemplate) => {
    setSelectedTemplate(template)
    await fetchTemplateSteps(template.id)
  }

  const handleCreateStep = async () => {
    if (!selectedTemplate) return

    try {
      const token = localStorage.getItem('token')
      const stepData = editingStep ? { ...newStep, id: editingStep.id } : newStep
      const url = editingStep 
        ? `${API_BASE_URL}/api/workflows/steps/${editingStep.id}`
        : `${API_BASE_URL}/api/workflows/templates/${selectedTemplate.id}/steps`
      const method = editingStep ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(stepData)
      })

      if (response.ok) {
        const data = await response.json()
        if (editingStep) {
          // Update existing step
          setTemplateSteps(prev => 
            prev.map(step => 
              step.id === editingStep.id ? data.step : step
            ).sort((a, b) => a.position - b.position)
          )
          showMessage('success', 'Workflow step updated successfully!')
        } else {
          // Add new step
          setTemplateSteps(prev => [...prev, data.step].sort((a, b) => a.position - b.position))
          showMessage('success', 'Workflow step created successfully!')
        }
        
        setShowStepEditor(false)
        setEditingStep(null)
        setNewStep({
          name: '',
          description: '',
          color: '#3B82F6',
          is_start_step: false,
          is_end_step: false
        })
      } else {
        const error = await response.json()
        showMessage('error', error.message || `Failed to ${editingStep ? 'update' : 'create'} workflow step`)
      }
    } catch (error) {
      console.error(`Error ${editingStep ? 'updating' : 'creating'} step:`, error)
      showMessage('error', `Failed to ${editingStep ? 'update' : 'create'} workflow step`)
    }
  }

  const handleStepDragEnd = async (result: DropResult) => {
    if (!result.destination || !selectedTemplate) return

    const items = Array.from(templateSteps)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)

    // Update positions
    const updatedSteps = items.map((step, index) => ({
      ...step,
      position: index + 1
    }))

    setTemplateSteps(updatedSteps)

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/workflows/templates/${selectedTemplate.id}/steps/reorder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          steps: updatedSteps.map(step => ({ id: step.id, position: step.position }))
        })
      })

      if (!response.ok) {
        // Revert on error
        await fetchTemplateSteps(selectedTemplate.id)
        showMessage('error', 'Failed to reorder steps')
      }
    } catch (error) {
      console.error('Error reordering steps:', error)
      await fetchTemplateSteps(selectedTemplate.id)
      showMessage('error', 'Failed to reorder steps')
    }
  }

  const handleDeleteStep = async (step: WorkflowStep) => {
    if (!confirm(`Are you sure you want to delete the step "${step.name}"? This action cannot be undone.`)) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/workflows/steps/${step.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        setTemplateSteps(prev => prev.filter(s => s.id !== step.id))
        showMessage('success', 'Workflow step deleted successfully!')
      } else {
        const error = await response.json()
        showMessage('error', error.message || 'Failed to delete workflow step')
      }
    } catch (error) {
      console.error('Error deleting step:', error)
      showMessage('error', 'Failed to delete workflow step')
    }
  }

  const getDealVersionLabel = (version: 'commercial' | 'residential') => {
    return t(`settings.workflowManagement.dealVersions.${version}`)
  }

  const getActionTypeLabel = (actionType: string) => {
    const labels = {
      'send_email': 'Send Email',
      'send_sms': 'Send SMS',
      'create_task': 'Create Task',
      'send_notification': 'Send Notification'
    }
    return labels[actionType as keyof typeof labels] || actionType
  }

  const getTriggerEventLabel = (event: string) => {
    const labels = {
      'on_enter': 'When entering step',
      'on_exit': 'When leaving step',
      'after_delay': 'After delay'
    }
    return labels[event as keyof typeof labels] || event
  }

  if (loading) {
    return (
      <PageContent>
        <LoadingSpinner />
      </PageContent>
    )
  }

  return (
    <PageContent>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
              {t('settings.workflowManagement.title')}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('settings.workflowManagement.description')}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                setEditingNotificationTemplate(null)
                setShowTemplateEditor(true)
              }}
              className="px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all flex items-center space-x-2 shadow-sm"
            >
              <SettingsIcon className="h-4 w-4" />
              <span className="text-sm">{t('settings.workflowManagement.templates')}</span>
            </button>
            <button
              onClick={() => setShowCreateTemplate(true)}
              className="px-4 py-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg hover:from-gray-900 hover:to-purple-700 transition-all flex items-center space-x-2 shadow-md hover:shadow-lg"
            >
              <PlusIcon className="h-5 w-5" />
              <span className="font-medium">{t('settings.workflowManagement.createWorkflow')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 p-3 rounded-lg flex items-center space-x-3 ${
          message.type === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800' 
            : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
        }`}>
          <div className={`w-1.5 h-8 rounded-full ${message.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}></div>
          <span className="text-sm font-medium">{message.text}</span>
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Workflow Templates Sidebar */}
        <div className="lg:w-80 flex-shrink-0">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden sticky top-4">
            <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                {t('settings.workflowManagement.workflowTemplates')}
              </h2>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                {t('settings.workflowManagement.templatesCreated', { count: workflowTemplates.length })}
              </p>
            </div>
            <div className="p-3 max-h-[calc(100vh-250px)] overflow-y-auto">
              {workflowTemplates.length === 0 ? (
                <div className="text-center py-6 px-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('settings.workflowManagement.noWorkflowsDescription')}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {workflowTemplates.map((template) => (
                    <div
                      key={template.id}
                      onClick={() => handleSelectTemplate(template)}
                      className={`group p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedTemplate?.id === template.id
                          ? 'border-gray-900 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 shadow-sm'
                          : 'border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-gray-900 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-sm text-gray-900 dark:text-white truncate">
                            {template.name}
                          </h3>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 truncate">
                            {template.deal_type_name}
                          </p>
                        </div>
                        <div className="flex items-center space-x-1.5 ml-2">
                          <div className={`w-2 h-2 rounded-full ${
                            template.is_active ? 'bg-green-500' : 'bg-gray-400'
                          }`} />
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                const dropdownId = `template-dropdown-${template.id}`
                                const dropdown = document.getElementById(dropdownId)
                                if (dropdown) {
                                  dropdown.classList.toggle('hidden')
                                }
                              }}
                              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors opacity-0 group-hover:opacity-100"
                            >
                              <EllipsisVerticalIcon className="h-4 w-4" />
                            </button>
                            <div
                              id={`template-dropdown-${template.id}`}
                              className="hidden absolute right-0 top-6 bg-white dark:bg-gray-700 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600 z-20 min-w-[140px]"
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setEditingWorkflowTemplate(template)
                                  setNewTemplate({
                                    name: template.name,
                                    description: template.description || '',
                                    deal_type_ids: [template.deal_type_id],
                                    include_commercial: template.deal_version === 'commercial',
                                    include_residential: template.deal_version === 'residential'
                                  })
                                  setShowEditTemplate(true)
                                  document.getElementById(`template-dropdown-${template.id}`)?.classList.add('hidden')
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center space-x-2 rounded-t-lg"
                              >
                                <EditIcon className="h-4 w-4" />
                                <span>{t('settings.workflowManagement.edit')}</span>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDeleteTemplate(template)
                                  document.getElementById(`template-dropdown-${template.id}`)?.classList.add('hidden')
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-2 rounded-b-lg"
                              >
                                <TrashIcon className="h-4 w-4" />
                                <span>{t('settings.workflowManagement.delete')}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap gap-1">
                          {(template.deal_versions || [template.deal_version]).map((version, idx) => (
                            <span key={idx} className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              version === 'commercial'
                                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                                : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                            }`}>
                              {getDealVersionLabel(version)}
                            </span>
                          ))}
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {t('settings.workflowManagement.steps', { count: template.step_count })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Deal Statuses Canvas */}
        <div className="flex-1 min-w-0">
          {selectedTemplate ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {selectedTemplate.name}
                    </h2>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                      Configure automations for each deal status
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setEditingWorkflowTemplate(selectedTemplate)
                      setShowEditTemplate(true)
                    }}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 rounded-lg hover:bg-white/50 dark:hover:bg-gray-700/50 transition-all"
                  >
                    <SettingsIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="p-6">
                {dealStatuses.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <ChartIcon className="h-10 w-10 text-gray-900 dark:text-gray-600" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      No Deal Statuses Found
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                      Create deal statuses in Deal Status Settings first to configure workflow automations.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dealStatuses.map((status, index) => {
                      // Determine which flags this status has
                      const flags: { label: string; color: string }[] = []
                      if (status.is_lead) flags.push({ label: 'Lead', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' })
                      if (status.is_submitted) flags.push({ label: 'Submitted', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' })
                      if (status.is_approved) flags.push({ label: 'Approved', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' })
                      if (status.is_funded) flags.push({ label: 'Funded', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' })
                      if (status.is_closed) flags.push({ label: 'Closed', color: 'bg-gray-100 text-gray-700 dark:bg-gray-600/30 dark:text-gray-300' })
                      if (status.is_cancelled) flags.push({ label: 'Cancelled', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' })
                      if (status.is_archived) flags.push({ label: 'Archived', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' })
                      
                      // Find matching workflow step for this status (if any)
                      const matchingStep = templateSteps.find(step => 
                        step.name.toLowerCase() === status.name.toLowerCase()
                      )
                      
                      return (
                        <div
                          key={status.id}
                          className="group flex items-center p-4 bg-gray-50 dark:bg-gray-700/30 rounded-xl border border-gray-200 dark:border-gray-600 hover:shadow-md transition-all"
                        >
                          {/* Position indicator */}
                          <div className="flex flex-col items-center mr-4">
                            <div
                              className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center font-bold text-white shadow-sm"
                              style={{ backgroundColor: status.color }}
                            >
                              {index + 1}
                            </div>
                            {index < dealStatuses.length - 1 && (
                              <div className="w-0.5 h-4 mt-2 bg-gradient-to-b from-gray-300 to-transparent dark:from-gray-500"></div>
                            )}
                          </div>
                          
                          {/* Status info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center flex-wrap gap-2 mb-1">
                              <h3 className="font-semibold text-gray-900 dark:text-white">
                                {status.name}
                              </h3>
                              {/* Display flags */}
                              {flags.map((flag, flagIndex) => (
                                <span
                                  key={flagIndex}
                                  className={`px-2 py-0.5 text-xs rounded-md font-medium ${flag.color}`}
                                >
                                  {flag.label}
                                </span>
                              ))}
                            </div>
                            {matchingStep?.automations && matchingStep.automations.length > 0 && (
                              <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400">
                                <CogIcon className="h-3.5 w-3.5" />
                                <span>{matchingStep.automations.length} automation{matchingStep.automations.length > 1 ? 's' : ''} configured</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Action button */}
                          <button
                            onClick={() => {
                              // Create or use existing step for this status
                              const stepForStatus: WorkflowStep = matchingStep || {
                                id: status.id,
                                workflow_template_id: selectedTemplate.id,
                                name: status.name,
                                description: '',
                                color: status.color,
                                position: index + 1,
                                is_start_step: index === 0,
                                is_end_step: index === dealStatuses.length - 1,
                                automations: []
                              }
                              setSelectedStepForAutomation(stepForStatus)
                              setShowAutomationEditor(true)
                            }}
                            className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-all opacity-0 group-hover:opacity-100 flex items-center space-x-1.5"
                          >
                            <CogIcon className="h-4 w-4" />
                            <span>Configure</span>
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-800 rounded-xl shadow-sm border-2 border-dashed border-gray-300 dark:border-gray-600">
              <div className="p-16 text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <ChartIcon className="h-12 w-12 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {t('settings.workflowManagement.selectWorkflowTemplate')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
                  {t('settings.workflowManagement.selectDescription')}
                </p>
                <button
                  onClick={() => setShowCreateTemplate(true)}
                  className="px-6 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl hover:from-gray-900 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl font-semibold"
                >
                  {t('settings.workflowManagement.createNewWorkflow')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status Rules Section */}
      <StatusRulesSection
        statuses={dealStatuses}
        onError={(msg) => showMessage('error', msg)}
        onSuccess={(msg) => showMessage('success', msg)}
      />

      {/* Create Template Modal */}
      {showCreateTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center shadow-md">
                  <PlusIcon className="h-4 w-4 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {t('settings.workflowManagement.createModal.title')}
                </h2>
              </div>
              <button
                onClick={() => setShowCreateTemplate(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-white/50 dark:hover:bg-gray-700/50 transition-all"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <form className="space-y-5">
                {/* Workflow Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('settings.workflowManagement.createModal.workflowName')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-colors"
                    placeholder={t('settings.workflowManagement.createModal.workflowNamePlaceholder')}
                  />
                </div>

                {/* Deal Types - Multi-select with checkboxes */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('settings.workflowManagement.createModal.dealType')}
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        if (newTemplate.deal_type_ids.length === dealTypes.length) {
                          setNewTemplate({ ...newTemplate, deal_type_ids: [] })
                        } else {
                          setNewTemplate({ ...newTemplate, deal_type_ids: dealTypes.map(dt => dt.id) })
                        }
                      }}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {newTemplate.deal_type_ids.length === dealTypes.length ? 'Deselect all' : 'Select all'}
                    </button>
                  </div>
                  <div className="space-y-2 max-h-32 overflow-y-auto p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    {dealTypes.map((type) => (
                      <label key={type.id} className="flex items-center space-x-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600/50 p-1.5 rounded-md transition-colors">
                        <input
                          type="checkbox"
                          checked={newTemplate.deal_type_ids.includes(type.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewTemplate({ ...newTemplate, deal_type_ids: [...newTemplate.deal_type_ids, type.id] })
                            } else {
                              setNewTemplate({ ...newTemplate, deal_type_ids: newTemplate.deal_type_ids.filter(id => id !== type.id) })
                            }
                          }}
                          className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{type.name}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {newTemplate.deal_type_ids.length === 0 
                      ? 'All deal types will be included' 
                      : `${newTemplate.deal_type_ids.length} deal type${newTemplate.deal_type_ids.length > 1 ? 's' : ''} selected`}
                  </p>
                </div>

                {/* Deal Versions - Checkboxes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('settings.workflowManagement.createModal.dealVersion')}
                  </label>
                  <div className="flex items-center space-x-6">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newTemplate.include_residential}
                        onChange={(e) => setNewTemplate({ ...newTemplate, include_residential: e.target.checked })}
                        className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{t('settings.workflowManagement.dealVersions.residential')}</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newTemplate.include_commercial}
                        onChange={(e) => setNewTemplate({ ...newTemplate, include_commercial: e.target.checked })}
                        className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{t('settings.workflowManagement.dealVersions.commercial')}</span>
                    </label>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('settings.workflowManagement.createModal.description')}
                  </label>
                  <textarea
                    value={newTemplate.description}
                    onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-colors resize-none"
                    placeholder={t('settings.workflowManagement.createModal.descriptionPlaceholder')}
                  />
                </div>
              </form>
            </div>
            <div className="flex items-center justify-end space-x-3 px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowCreateTemplate(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-all shadow-sm font-medium"
              >
                {t('settings.workflowManagement.createModal.cancel')}
              </button>
              <button
                onClick={handleCreateTemplate}
                disabled={!newTemplate.name || (!newTemplate.include_commercial && !newTemplate.include_residential)}
                className="px-5 py-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg hover:from-gray-900 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg font-semibold"
              >
                {t('settings.workflowManagement.createModal.createButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Template Modal */}
      {showEditTemplate && editingWorkflowTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center shadow-md">
                  <EditIcon className="h-4 w-4 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Edit Workflow Template
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowEditTemplate(false)
                  setEditingWorkflowTemplate(null)
                  resetNewTemplate()
                }}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-white/50 dark:hover:bg-gray-700/50 transition-all"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Workflow Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-colors"
                    placeholder="e.g., Purchase Residential Workflow"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Deal Type <span className="text-gray-500">(Read-only)</span>
                  </label>
                  <input
                    type="text"
                    value={editingWorkflowTemplate.deal_type_name}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Deal Version <span className="text-gray-500">(Read-only)</span>
                  </label>
                  <input
                    type="text"
                    value={getDealVersionLabel(editingWorkflowTemplate.deal_version)}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={newTemplate.description}
                    onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-colors"
                    placeholder="Optional description of this workflow..."
                  />
                </div>
              </form>
            </div>
            <div className="flex items-center justify-end space-x-3 px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowEditTemplate(false)
                  setEditingWorkflowTemplate(null)
                  resetNewTemplate()
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-all shadow-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleEditTemplate}
                disabled={!newTemplate.name}
                className="px-5 py-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg hover:from-gray-900 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg font-semibold"
              >
                Update Template
              </button>
            </div>
          </div>
        </div>
      )}
      {showStepEditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center shadow-md">
                  {editingStep ? <EditIcon className="h-4 w-4 text-white" /> : <PlusIcon className="h-4 w-4 text-white" />}
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {editingStep ? 'Edit Workflow Step' : 'Add Workflow Step'}
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowStepEditor(false)
                  setEditingStep(null)
                }}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-white/50 dark:hover:bg-gray-700/50 transition-all"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Step Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newStep.name}
                    onChange={(e) => setNewStep({ ...newStep, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-colors"
                    placeholder="e.g., Simple Lead, Document Collection, Closed Deal"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={newStep.description}
                    onChange={(e) => setNewStep({ ...newStep, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-colors"
                    placeholder="Optional description of this step..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Step Color
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={newStep.color}
                      onChange={(e) => setNewStep({ ...newStep, color: e.target.value })}
                      className="w-16 h-10 rounded-md border border-gray-300 dark:border-gray-600 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={newStep.color}
                      onChange={(e) => setNewStep({ ...newStep, color: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-colors"
                      placeholder="#3B82F6"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_start_step"
                      checked={newStep.is_start_step}
                      onChange={(e) => setNewStep({ ...newStep, is_start_step: e.target.checked })}
                      className="h-4 w-4 text-gray-900 border-gray-300 dark:border-gray-600 rounded focus:ring-gray-900"
                    />
                    <label htmlFor="is_start_step" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Start Step
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_end_step"
                      checked={newStep.is_end_step}
                      onChange={(e) => setNewStep({ ...newStep, is_end_step: e.target.checked })}
                      className="h-4 w-4 text-gray-900 border-gray-300 dark:border-gray-600 rounded focus:ring-gray-900"
                    />
                    <label htmlFor="is_end_step" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      End Step
                    </label>
                  </div>
                </div>
              </form>
            </div>
            <div className="flex items-center justify-end space-x-3 px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowStepEditor(false)
                  setEditingStep(null)
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-all shadow-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateStep}
                disabled={!newStep.name}
                className="px-5 py-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg hover:from-gray-900 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg font-semibold"
              >
                {editingStep ? 'Update Step' : 'Create Step'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Automation Rule Builder */}
      {showAutomationEditor && selectedStepForAutomation && (
        <EnhancedAutomationRuleBuilder
          stepId={selectedStepForAutomation.id}
          existingRules={selectedStepForAutomation.automations || []}
          onSave={async (rules, requiredFields) => {
            try {
              const token = localStorage.getItem('token')
              
              // Save required fields first
              if (requiredFields) {
                const reqFieldsResponse = await fetch(`${API_BASE_URL}/api/workflows/steps/${selectedStepForAutomation.id}/required-fields`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({ required_fields: requiredFields })
                })

                if (!reqFieldsResponse.ok) {
                  const error = await reqFieldsResponse.json()
                  showMessage('error', error.message || t('settings.workflowManagement.messages.validationSaveError'))
                  return
                }
              }

              // Save automation rules
              const response = await fetch(`${API_BASE_URL}/api/workflows/automations`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  step_id: selectedStepForAutomation.id,
                  automations: rules
                })
              })

              if (response.ok) {
                showMessage('success', t('settings.workflowManagement.messages.configSaved'))
                setShowAutomationEditor(false)
                setSelectedStepForAutomation(null)
                
                // Refresh the selected template to show updated automations
                if (selectedTemplate) {
                  await fetchTemplateSteps(selectedTemplate.id)
                }
              } else {
                const error = await response.json()
                showMessage('error', error.message || t('settings.workflowManagement.messages.automationSaveError'))
              }
            } catch (error) {
              console.error('Error saving configuration:', error)
              showMessage('error', t('settings.workflowManagement.messages.saveError'))
            }
          }}
          onCancel={() => {
            setShowAutomationEditor(false)
            setSelectedStepForAutomation(null)
          }}
          notificationTemplates={notificationTemplates}
        />
      )}

      {/* Notification Template Editor */}
      {showTemplateEditor && (
        <NotificationTemplateEditor
          isOpen={showTemplateEditor}
          editingTemplate={editingNotificationTemplate}
          onSave={async (template) => {
            try {
              const token = localStorage.getItem('token')
              const method = editingNotificationTemplate ? 'PUT' : 'POST'
              const url = editingNotificationTemplate 
                ? `${API_BASE_URL}/api/workflows/notification-templates/${editingNotificationTemplate.id}`
                : `${API_BASE_URL}/api/workflows/notification-templates`
              
              const response = await fetch(url, {
                method,
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(template)
              })
              
              if (response.ok) {
                showMessage('success', `Notification template ${editingNotificationTemplate ? 'updated' : 'created'} successfully!`)
                await fetchNotificationTemplates()
              } else {
                throw new Error('Failed to save template')
              }
            } catch (error) {
              console.error('Error saving notification template:', error)
              showMessage('error', 'Failed to save notification template')
            }
          }}
          onClose={() => {
            setShowTemplateEditor(false)
            setEditingNotificationTemplate(null)
          }}
        />
      )}
    </PageContent>
  )
}

export default WorkflowManagement